/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * What kind of event the entry records: 'configuration' for a change to the platform's configuration, 'security' for machine access and other security-relevant activity that is not a configuration decision. Always present in responses; optional in the schema so clients built against it still read responses from servers that predate it.
 */
export const AuditLogCategory = {
  Configuration: "configuration",
  Security: "security",
} as const;
/**
 * What kind of event the entry records: 'configuration' for a change to the platform's configuration, 'security' for machine access and other security-relevant activity that is not a configuration decision. Always present in responses; optional in the schema so clients built against it still read responses from servers that predate it.
 */
export type AuditLogCategory = OpenEnum<typeof AuditLogCategory>;

export type AuditLog = {
  /**
   * The registered OAuth client the call authenticated as, when it had one. Absent for calls that carried no OAuth client.
   */
  actingClientId?: string | undefined;
  /**
   * How the change was made: 'dashboard', 'api_key', 'platform_mcp', 'project_assistant', or 'unknown' when no surface was identifiable. Always present.
   */
  actingSurface: string;
  action: string;
  actorDisplayName?: string | undefined;
  actorId: string;
  actorSlug?: string | undefined;
  actorType: string;
  afterSnapshot?: any | undefined;
  beforeSnapshot?: any | undefined;
  /**
   * What kind of event the entry records: 'configuration' for a change to the platform's configuration, 'security' for machine access and other security-relevant activity that is not a configuration decision. Always present in responses; optional in the schema so clients built against it still read responses from servers that predate it.
   */
  category?: AuditLogCategory | undefined;
  /**
   * The creation date of the audit log.
   */
  createdAt: Date;
  id: string;
  metadata?: { [k: string]: any } | undefined;
  projectId?: string | undefined;
  projectSlug?: string | undefined;
  subjectDisplayName?: string | undefined;
  subjectId: string;
  subjectSlug?: string | undefined;
  subjectType: string;
};

/** @internal */
export const AuditLogCategory$inboundSchema: z.ZodMiniType<
  AuditLogCategory,
  unknown
> = openEnums.inboundSchema(AuditLogCategory);

/** @internal */
export const AuditLog$inboundSchema: z.ZodMiniType<AuditLog, unknown> = z.pipe(
  z.object({
    acting_client_id: z.optional(z.string()),
    acting_surface: z.string(),
    action: z.string(),
    actor_display_name: z.optional(z.string()),
    actor_id: z.string(),
    actor_slug: z.optional(z.string()),
    actor_type: z.string(),
    after_snapshot: z.optional(z.any()),
    before_snapshot: z.optional(z.any()),
    category: z.optional(AuditLogCategory$inboundSchema),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    id: z.string(),
    metadata: z.optional(z.record(z.string(), z.any())),
    project_id: z.optional(z.string()),
    project_slug: z.optional(z.string()),
    subject_display_name: z.optional(z.string()),
    subject_id: z.string(),
    subject_slug: z.optional(z.string()),
    subject_type: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "acting_client_id": "actingClientId",
      "acting_surface": "actingSurface",
      "actor_display_name": "actorDisplayName",
      "actor_id": "actorId",
      "actor_slug": "actorSlug",
      "actor_type": "actorType",
      "after_snapshot": "afterSnapshot",
      "before_snapshot": "beforeSnapshot",
      "created_at": "createdAt",
      "project_id": "projectId",
      "project_slug": "projectSlug",
      "subject_display_name": "subjectDisplayName",
      "subject_id": "subjectId",
      "subject_slug": "subjectSlug",
      "subject_type": "subjectType",
    });
  }),
);

export function auditLogFromJSON(
  jsonString: string,
): SafeParseResult<AuditLog, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AuditLog$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AuditLog' from JSON`,
  );
}
